// The storage seam for journey/tool progress (owner decision 2026-07-31:
// DB for durable facts, localStorage only for keystroke drafts and as the
// signed-in write-through cache).
//
// The ~20 *Progress.ts libs keep their synchronous read/write API and their
// versioned payloads; only the storage calls route here:
//   - Signed OUT: reads/writes hit the original bare localStorage key —
//     byte-identical to the old behavior (the free no-login funnel, tests).
//   - Signed IN: the cache key is scoped per user (`journey:<uid>:<key>`) so
//     siblings sharing a browser can't collide, every write also debounces an
//     upsert to the journey_progress table, and app load hydrates the cache
//     from the DB so progress follows the learner across devices.
//
// Last-write-wins per (user, key). The payload's own schemaVersion field keeps
// each lib's migration semantics — the store never inspects the value.
import type { SupabaseClient } from "@supabase/supabase-js";

const DEBOUNCE_MS = 1500;

let uid: string | null = null;
let hydratedFor: string | null = null;
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const pending = new Map<string, string>(); // key → latest raw value awaiting upsert

// @/lib/supabase pulls in the full Supabase JS SDK (~66KB, confirmed via a
// Lighthouse mobile audit as a first-party chunk that was 82% unused on first
// paint) — dynamically imported and cached here, same pattern and rationale
// as analytics.ts's posthog-js/wireIdentity, so it doesn't sit in the
// critical bundle reached from instrumentation-client.ts.
let supabase: SupabaseClient | undefined;
async function getSupabase(): Promise<SupabaseClient> {
  if (!supabase) {
    supabase = (await import("@/lib/supabase")).createClient();
  }
  return supabase;
}

// Address storage via `window.localStorage`, never the bare global: Node's
// experimental WebStorage global is disabled in the test workers (PR #103),
// and node-env lib tests stub `window.localStorage` only.
const isBrowser = (): boolean =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";
const scoped = (key: string): string => (uid ? `journey:${uid}:${key}` : key);

/** Synchronous read — the raw JSON string, exactly like localStorage.getItem. */
export function journeyRead(key: string): string | null {
  if (!isBrowser()) return null;
  const raw = window.localStorage.getItem(scoped(key));
  if (raw != null || !uid) return raw;
  // Signed-in cache miss: adopt a pre-scoping value left under the bare key
  // (this browser's progress from before the DB era). Cache-local only — the
  // next real write persists the (possibly converted) state to the DB.
  const legacy = window.localStorage.getItem(key);
  if (legacy != null) {
    try {
      window.localStorage.setItem(scoped(key), legacy);
    } catch {
      // storage full or disabled — reads still work off the bare key next time.
    }
  }
  return legacy;
}

/** Synchronous write — cache now, debounce a DB upsert when signed in. */
export function journeyWrite(key: string, value: string): void {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(scoped(key), value);
  } catch {
    // storage full or disabled — the DB write below still preserves progress.
  }
  if (uid) queueUpsert(key, value);
}

function queueUpsert(key: string, value: string): void {
  pending.set(key, value);
  clearTimeout(timers.get(key));
  timers.set(
    key,
    setTimeout(() => void flushKey(key), DEBOUNCE_MS),
  );
}

async function flushKey(key: string): Promise<void> {
  clearTimeout(timers.get(key));
  timers.delete(key);
  const value = pending.get(key);
  if (value == null || !uid) return;
  pending.delete(key);
  let state: unknown;
  try {
    state = JSON.parse(value);
  } catch {
    return; // not JSON — never ours; don't send it anywhere
  }
  const client = await getSupabase();
  const { error } = await client
    .from("journey_progress")
    .upsert({ user_id: uid, tool_key: key, state, updated_at: new Date().toISOString() });
  // On failure keep the value queued (unless a newer write superseded it) so a
  // later write or the pagehide flush can retry. The scoped cache already has
  // it, so same-device progress is never lost either way.
  if (error && !pending.has(key)) pending.set(key, value);
}

function flushAll(): void {
  for (const key of [...pending.keys()]) void flushKey(key);
}

async function hydrate(forUid: string): Promise<void> {
  if (hydratedFor === forUid) return;
  hydratedFor = forUid;
  const client = await getSupabase();
  const { data, error } = await client
    .from("journey_progress")
    .select("tool_key, state")
    .eq("user_id", forUid);
  if (error || !data) {
    hydratedFor = null; // retry on the next auth event
    return;
  }
  for (const row of data as { tool_key: string; state: unknown }[]) {
    // A key with a pending upsert is newer than the DB copy — don't clobber it.
    if (pending.has(row.tool_key) || uid !== forUid) continue;
    try {
      window.localStorage.setItem(`journey:${forUid}:${row.tool_key}`, JSON.stringify(row.state));
    } catch {
      // storage full or disabled — journeys fall back to blank on this device.
    }
  }
}

/**
 * Wire the store to auth state. Called once from instrumentation-client.ts
 * (same pattern as analytics' wireIdentity): identify on any session,
 * hydrate from the DB, detach on sign-out.
 */
export async function initJourneyStore(): Promise<void> {
  if (!isBrowser()) return;
  const client = await getSupabase();
  client.auth.onAuthStateChange((event, session) => {
    const nextUid = session?.user?.id ?? null;
    if (event === "SIGNED_OUT" || nextUid == null) {
      uid = null;
      hydratedFor = null;
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      pending.clear();
      return;
    }
    if (nextUid !== uid || hydratedFor !== nextUid) {
      uid = nextUid;
      // Deferred: Supabase warns against issuing client calls synchronously
      // inside onAuthStateChange (auth-lock deadlock). Also re-runs on any
      // later auth event (e.g. TOKEN_REFRESHED) while a failed hydration is
      // still outstanding — hydrate() itself dedupes the success case.
      setTimeout(() => void hydrate(nextUid), 0);
    }
  });
  // Debounced writes could be lost on a fast tab close — flush them the moment
  // the page hides. (Best effort: an in-flight request during unload can still
  // be dropped; the scoped cache keeps same-device progress correct and the
  // next session's writes catch the DB up.)
  window.addEventListener("pagehide", flushAll);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushAll();
  });
}

/** Test-only: reset module state between cases. */
export function resetJourneyStoreForTests(): void {
  uid = null;
  hydratedFor = null;
  supabase = undefined;
  for (const t of timers.values()) clearTimeout(t);
  timers.clear();
  pending.clear();
}
